import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useLocalStorage } from '../../auth/useLocalStorage';
import { headerData } from '../../data/headerCostum';
import { useReactToPrint } from 'react-to-print';
import { styled } from '@mui/material/styles';

// Styled components untuk laporan resmi pemerintahan
const ReportContainer = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  padding: '20mm',
  minHeight: '297mm', // A4 height
  width: '210mm', // A4 width
  margin: '0 auto',
  fontFamily: 'Times New Roman, serif',
  lineHeight: 1.6,
  color: '#000000',
  '@media print': {
    margin: 0,
    padding: '20mm',
    boxShadow: 'none',
    width: '100%',
    minHeight: '100vh',
  }
}));

const OfficialHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  borderBottom: '3px double #000000',
  paddingBottom: theme.spacing(3),
  marginBottom: theme.spacing(4),
}));

const ReportTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Times New Roman, serif',
  fontWeight: 'bold',
  color: '#000000',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
}));

const OfficialTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    border: '1px solid #000000',
    padding: theme.spacing(1.5),
    fontFamily: 'Times New Roman, serif',
    fontSize: '12pt',
    color: '#000000',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
    textAlign: 'center',
  }
}));

const InfoTable = styled(Table)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiTableCell-root': {
    border: 'none',
    borderBottom: '1px solid #000000',
    padding: theme.spacing(0.5, 1),
    fontFamily: 'Times New Roman, serif',
    fontSize: '12pt',
    color: '#000000',
  },
  '& .MuiTableCell-root:first-of-type': {
    fontWeight: 'bold',
    width: '150px',
  }
}));

const DocumentationGrid = styled(Grid)(({ theme }) => ({
  '& img': {
    width: '100%',
    height: '120px',
    objectFit: 'cover',
    border: '1px solid #000000',
    borderRadius: 0,
  }
}));

const PrintPDFLokasi = ({ execute }) => {
  const [longwis] = useLocalStorage("longwis");
  const [dataLongwis, setDataLongwis] = useState(null);
  const [coords, setCoords] = useState(null);
  const [dokumentasiLOKASI, setdokumentasiLOKASI] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloaded, setDownloaded] = useState(false);

  const pdfRef = useRef();

  const DownloadPDF = useReactToPrint({
    content: () => pdfRef.current,
    pageStyle: `
      @page {
        size: A4;
        margin: 20mm;
      }
      @media print {
        body { -webkit-print-color-adjust: exact; }
      }
    `
  });

  // const formatDate = (value) => {
  //   const date_time = new Date(value);
  //   return (date_time.toISOString().split('T')[0]);
  // }


  const getLaporanData = useCallback(() => {
    setLoading(true);
    setError(null);
    
    fetch(process.env.REACT_APP_API_URL + "api/longwis/" + longwis?.id, {
      method: "get",
      headers: headerData
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Gagal mengambil data laporan');
        }
        return res.json();
      })
      .then((res) => {
        if (res.nama_longwis) {
          setDataLongwis(res);
          
          // Safe JSON parsing for file_foto
          if (res.LokLongwi?.file_foto) {
            try {
              const filePhotoData = typeof res.LokLongwi.file_foto === 'string' 
                ? res.LokLongwi.file_foto.trim() 
                : res.LokLongwi.file_foto;
              
              if (filePhotoData && filePhotoData !== '') {
                const parsedPhotos = JSON.parse(filePhotoData);
                setdokumentasiLOKASI(Array.isArray(parsedPhotos) ? parsedPhotos : []);
              }
            } catch (error) {
              console.warn('Error parsing file_foto JSON:', error);
              console.warn('Raw file_foto data:', res.LokLongwi.file_foto);
              setdokumentasiLOKASI([]); // Set empty array as fallback
            }
          }
          
          // Safe JSON parsing for lokasi
          if (res.lokasi) {
            try {
              const lokasiData = typeof res.lokasi === 'string' 
                ? res.lokasi.trim() 
                : res.lokasi;
              
              if (lokasiData && lokasiData !== '') {
                const parsedCoords = JSON.parse(lokasiData);
                setCoords(Array.isArray(parsedCoords) && parsedCoords.length >= 2 ? parsedCoords : null);
              }
            } catch (error) {
              console.warn('Error parsing lokasi JSON:', error);
              console.warn('Raw lokasi data:', res.lokasi);
              setCoords(null); // Set null as fallback
            }
          }
        } else {
          throw new Error('Data laporan tidak ditemukan');
        }
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setError(err.message || 'Terjadi kesalahan saat mengambil data');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [longwis?.id]);

  useEffect(() => {
    if (longwis?.id && !dataLongwis) {
      getLaporanData();
    } else if (dataLongwis && execute && !downloaded) {
      DownloadPDF();
      setDownloaded(true);
    }
  }, [dataLongwis, getLaporanData, DownloadPDF, downloaded, execute, longwis?.id]);

  // Loading state
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={60} sx={{ color: '#1dc39f' }} />
        <Typography variant="h6" color="text.secondary">
          Memuat data laporan...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ borderRadius: '12px' }}>
          <Typography variant="h6" gutterBottom>
            Terjadi Kesalahan
          </Typography>
          <Typography variant="body1">
            {error}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // No data state
  if (!dataLongwis) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ borderRadius: '12px' }}>
          <Typography variant="h6" gutterBottom>
            Data Tidak Ditemukan
          </Typography>
          <Typography variant="body1">
            Data laporan lokasi tidak tersedia.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <ReportContainer ref={pdfRef}>
      {/* Header Resmi Pemerintahan */}
      <OfficialHeader>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={2}>
            <Box
              sx={{
                width: 80,
                height: 80,
                border: '2px solid #000000',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                backgroundColor: 'white'
              }}
            >
              <BusinessIcon sx={{ fontSize: 40, color: '#000000' }} />
            </Box>
          </Grid>
          <Grid item xs={8}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontFamily: 'Times New Roman, serif',
                fontWeight: 'bold',
                color: '#000000',
                textTransform: 'uppercase',
                lineHeight: 1.2
              }}
            >
              PEMERINTAH KOTA MAKASSAR
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: 'Times New Roman, serif',
                fontWeight: 'bold',
                color: '#000000',
                textTransform: 'uppercase',
                fontSize: '14pt'
              }}
            >
              Dinas Komunikasi dan Informatika
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'Times New Roman, serif',
                color: '#000000',
                fontSize: '10pt',
                mt: 1
              }}
            >
              Jalan Andi Pangeran Pettarani No. 62 Makassar 90232<br/>
              Email: diskominfo@makassarkota.go.id
            </Typography>
          </Grid>
          <Grid item xs={2}>
            {/* Space for official seal/logo if needed */}
          </Grid>
        </Grid>
      </OfficialHeader>

      {/* Judul Laporan */}
      <ReportTitle variant="h5" align="center">
        LAPORAN PENGECEKAN LOKASI LORONG WISATA
      </ReportTitle>

      {/* Informasi Dasar */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontFamily: 'Times New Roman, serif',
            fontWeight: 'bold',
            color: '#000000',
            mb: 2,
            fontSize: '14pt'
          }}
        >
          I. INFORMASI UMUM
        </Typography>
        
        <TableContainer>
          <InfoTable>
            <TableBody>
              <TableRow>
                <TableCell>Nama PIC</TableCell>
                <TableCell>: {dataLongwis.nama_pic}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>No. Telp PIC</TableCell>
                <TableCell>: {dataLongwis.kontak_pic}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Nama Longwis</TableCell>
                <TableCell>: {dataLongwis.nama_longwis}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Kecamatan</TableCell>
                <TableCell>: {dataLongwis.kecamatan}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Kelurahan</TableCell>
                <TableCell>: {dataLongwis.kelurahan}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Alamat</TableCell>
                <TableCell>: {dataLongwis.alamat}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Tanggal Pengecekan</TableCell>
                <TableCell>: {new Date().toLocaleDateString('id-ID', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}</TableCell>
              </TableRow>
            </TableBody>
          </InfoTable>
        </TableContainer>
      </Box>

      {/* Detail Pengecekan */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontFamily: 'Times New Roman, serif',
            fontWeight: 'bold',
            color: '#000000',
            mb: 2,
            fontSize: '14pt'
          }}
        >
          II. DETAIL PENGECEKAN
        </Typography>

        <TableContainer>
          <OfficialTable>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ width: '20%' }}>
                  <Typography fontWeight="bold">JENIS PENGECEKAN</Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '25%' }}>
                  <Typography fontWeight="bold">LOKASI</Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '30%' }}>
                  <Typography fontWeight="bold">KETERANGAN</Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: '25%' }}>
                  <Typography fontWeight="bold">DOKUMENTASI</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="center">
                  <Typography sx={{ fontWeight: 'bold' }}>
                    LORONG WISATA
                  </Typography>
                </TableCell>
                
                <TableCell align="center">
                  {coords ? (
                    <Box sx={{ 
                      width: '100%',
                      height: '150px',
                      border: '1px solid #000000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <iframe 
                        title="lokasi" 
                        src={`https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3973.7270551547695!2d${coords[1]}!3d${coords[0]}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zNcKwMDgnNTAuNiJTIDExOcKwMjUnNDguOSJF!5e0!3m2!1sid!2sid!4v1723517260868!5m2!1sid!2sid`}
                        width="100%" 
                        height="140" 
                        style={{ border: 0 }}
                        loading="lazy" 
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Koordinat: -<br/>
                      Lokasi tidak tersedia
                    </Typography>
                  )}
                </TableCell>
                
                <TableCell>
                  <Typography sx={{ textAlign: 'justify', lineHeight: 1.5 }}>
                    {dataLongwis.LokLongwi?.keterangan || 'Tidak ada keterangan tambahan mengenai lokasi pengecekan.'}
                  </Typography>
                </TableCell>
                
                <TableCell align="center">
                  {dokumentasiLOKASI && dokumentasiLOKASI.length > 0 ? (
                    <DocumentationGrid container spacing={1}>
                      {dokumentasiLOKASI.map((element, index) => (
                        <Grid item xs={6} key={index}>
                          <img
                            alt={`Dokumentasi ${index + 1}`}
                            src={`${process.env.REACT_APP_API_URL}uploads/lokasi/${element}`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              textAlign: 'center',
                              mt: 0.5,
                              fontSize: '8pt'
                            }}
                          >
                            Foto {index + 1}
                          </Typography>
                        </Grid>
                      ))}
                    </DocumentationGrid>
                  ) : (
                    <Box sx={{ 
                      textAlign: 'center',
                      py: 3,
                      border: '1px dashed #000000',
                      minHeight: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="body2">
                        Tidak ada dokumentasi<br/>
                        foto tersedia
                      </Typography>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </OfficialTable>
        </TableContainer>
      </Box>

      {/* Footer Resmi */}
      <Box sx={{ 
        mt: 6,
        pt: 3,
        borderTop: '1px solid #000000'
      }}>
        <Grid container>
          <Grid item xs={8}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'Times New Roman, serif',
                fontSize: '10pt',
                color: '#000000'
              }}
            >
              Laporan ini dibuat secara otomatis oleh sistem<br/>
              Dinas Komunikasi dan Informatika Kota Makassar
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'right' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'Times New Roman, serif',
                fontSize: '10pt',
                color: '#000000'
              }}
            >
              Makassar, {new Date().toLocaleDateString('id-ID', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}<br/>
              <br/>
              <br/>
              <Box sx={{ borderBottom: '1px solid #000000', width: '120px', ml: 'auto', mb: 0.5 }} />
              <Typography variant="body2" fontWeight="bold">
                Petugas Pengecekan
              </Typography>
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </ReportContainer>
  );
}

export default PrintPDFLokasi;
